import { CpuChipIcon, HandRaisedIcon, UserIcon, WrenchScrewdriverIcon } from "@heroicons/react/16/solid";
import type { ComponentType, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import { stepKindText } from "../inputCards/typeWords";
import { addsPersonInputMark, stepKindOfDefinition, type StepDefinitionKind, type StepKind } from "./stepKind";

const ICONS: Readonly<Record<StepKind, ComponentType<SVGProps<SVGSVGElement>>>> = {
  person: UserIcon,
  agent: CpuChipIcon,
  tool: WrenchScrewdriverIcon,
};

/**
 * Whose step it is, at a glance (F61, F95): the kind's icon in its theme colour, and its word
 * from the process ontology (`stepKindText`). The same mark on the graph's cards, in the list
 * view and in a Run's steps, so a person's steps read apart from an agent's and a tool's
 * without reading a prefix. `needsPerson`: the placement's axis as module B's plan says it
 * (WD-1+3). The mark adds only what its kind doesn't already say, as module B's table of the
 * kinds says it (`stepKindAxes`): a raised hand on a step that needs a person's input although
 * its kind doesn't, its words on hover. Before the table arrives it adds nothing.
 */
export default function StepKindMark({ kind, needsPerson = false, className = "" }: { kind: StepDefinitionKind; needsPerson?: boolean; className?: string }) {
  const { t } = useTranslation();
  const shown = stepKindOfDefinition(kind);
  if (shown === null) return null;
  const Icon = ICONS[shown];
  return (
    <span className={classNames("app-step-kind-mark inline-flex items-center gap-1 text-xs", `app-step-kind-${shown}`, className)}>
      <Icon className="app-step-kind-icon h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{stepKindText(kind, t)}</span>
      {addsPersonInputMark(kind, needsPerson) ? <PersonInputMark words={t("workflows.axes.needsPerson")} /> : null}
    </span>
  );
}

/** "Person input needed": a raised hand, its words on hover and for screen readers. The legend draws the same mark. */
export function PersonInputMark({ words }: { words: string }) {
  return <HandRaisedIcon className="app-step-person-input h-3.5 w-3.5 shrink-0" role="img" aria-label={words} title={words} />;
}
